import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import { CircleOff } from "lucide-react-native";
import {
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useDietSupplements } from "../DietSupplementsContext";
import type { SupplementDetail } from "../DietSupplementsContext";
import type { DietSupplementsStackParamList } from "../DietSupplementsNavigator";
import { SUPPLEMENT_OPTIONS } from "../options";
import type { SupplementType } from "../options";

export function SupplementsScreen() {
  const t = useTranslations();
  const { state, updateState } = useDietSupplements();
  const navigation =
    useNavigation<NavigationProp<DietSupplementsStackParamList>>();

  const selectedSupplements = useMemo(
    () => new Set(state.supplements.map((s) => s.supplement)),
    [state.supplements],
  );

  const isNone = state.hasDietSupplements === false;

  const toggleSupplement = (supplement: SupplementType) => {
    if (selectedSupplements.has(supplement)) {
      // Remove
      const toRemove = state.supplements.find(
        (s) => s.supplement === supplement,
      );
      const idsToDelete = toRemove?.id
        ? [...state.supplementIdsToDelete, toRemove.id]
        : state.supplementIdsToDelete;
      const remaining = state.supplements.filter(
        (s) => s.supplement !== supplement,
      );
      updateState({
        hasDietSupplements: remaining.length > 0 ? true : null,
        supplements: remaining,
        supplementIdsToDelete: idsToDelete,
      });
    } else {
      // Add
      const newSupplement: SupplementDetail = { supplement };
      updateState({
        hasDietSupplements: true,
        supplements: [...state.supplements, newSupplement],
      });
    }
  };

  const handleNone = () => {
    const idsToDelete = state.supplements.filter((s) => s.id).map((s) => s.id!);
    updateState({
      hasDietSupplements: false,
      supplements: [],
      supplementIdsToDelete: [...state.supplementIdsToDelete, ...idsToDelete],
    });
  };

  const handleSave = () => {
    if (state.hasDietSupplements === true && state.supplements.length > 0) {
      navigation.navigate("SupplementsDetails");
    } else {
      navigation.navigate("DietSupplementsComplete");
    }
  };

  const isValid =
    isNone ||
    (state.hasDietSupplements === true && state.supplements.length > 0);

  return (
    <QuestionLayout
      title={t("med_history.lifestyle.diet_supplements.supplements_title")}
      subtitle={t(
        "med_history.lifestyle.diet_supplements.supplements_subtitle",
      )}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={!isValid}
      saveLabel={t("med_history.lifestyle.next")}
    >
      <View style={styles.container}>
        <View style={styles.grid}>
          {SUPPLEMENT_OPTIONS.map((option) => (
            <View key={option.id} style={styles.cell}>
              <SquareOptionButton
                icon={option.icon}
                label={t(option.labelKey)}
                isSelected={!isNone && selectedSupplements.has(option.id)}
                onPress={() => {
                  if (isNone) {
                    updateState({
                      hasDietSupplements: true,
                      supplements: [{ supplement: option.id }],
                    });
                  } else {
                    toggleSupplement(option.id);
                  }
                }}
                labelSize="sm"
              />
            </View>
          ))}
          <View style={styles.cell}>
            <SquareOptionButton
              icon={CircleOff}
              label={t("med_history.lifestyle.none")}
              isSelected={isNone}
              onPress={handleNone}
              labelSize="sm"
            />
          </View>
        </View>
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    width: "100%",
  },
  cell: {
    width: "47%",
  },
});
